//TransactionsCryptoWalletPage
import { FC, useContext, useEffect, useState } from "react";
import GlobalContext from "src/context/global-context";
import { useIsMounted } from "src/lib/hooks/use-is-mounted";
import { useMutation } from "react-query";
import client from "../../api";
import { newPaginationInput, PaginationInput, User } from "src/api/types";
import {
  APISomethingWrongMsg,
  LIST_PAGE_SIZE,
  NetworkDisconnectMsg,
} from "src/lib/constants";
import {
  capitalizeEachFirstLetter,
  capitalizeFirstLetter,
  getErrorMessage,
  logger,
  makeNumberWithFloatingDigits,
} from "src/lib/utils";
import { Table } from "antd";
import { ColumnsType } from "antd/es/table";
import { TransactionDataType } from "src/lib/constants/table-type";
import { useModalAction } from "../modal-views/context";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import routes from "src/config/routes";

type TransactionsCryptoWalletPageProps = {
  user: User;
  onSelectedInvoiceData: any;
};

const TransactionsCryptoWalletPage: FC<TransactionsCryptoWalletPageProps> = ({
  user,
  onSelectedInvoiceData,
}) => {
  const navigate = useNavigate();

  const { setIsAlertOpened, setAlertText } = useContext(GlobalContext);
  const isMounted = useIsMounted();
  const [isMobile, setIsMobile] = useState<boolean>(false);

  const [dataSource, setDataSource] = useState<TransactionDataType[]>([]);
  const [searchParams, setSearchParams] = useState<newPaginationInput>({
    // type: "wallet",
    limit: LIST_PAGE_SIZE,
    page: 1,
  });

  const [total, setTotal] = useState(0);

  useEffect(() => {
    isMounted &&
      getList({
        search_field: "user_id",
        search_value: user.id,
        page: searchParams.page,
        limit: searchParams.limit,
      });
  }, [isMounted, searchParams.page]);

  useEffect(() => {
    updateWindowSize();
    window.addEventListener("resize", updateWindowSize);

    return () => window.removeEventListener("resize", updateWindowSize);
  }, []);

  function updateWindowSize() {
    setIsMobile(window.innerWidth < 768 ? true : false);
  }

  const onPageChange = (page: number, pageSize: number) => {
    setSearchParams({ ...searchParams, page: page });
  };

  const { mutate: getList, isLoading } = useMutation(
    client.walletTerminalTransactions.getWalletTerminalTransactionsList,
    {
      onSuccess: (data: any) => {
        const dataObj = data;
        setTotal(dataObj.totalcount);
        setDataSource(
          dataObj?.items.map((item: any) => ({
            key: item?.id,
            id: item?.id,
            provider_identifier: item?.provider_identifier,
            user_id: item?.user_id,
            email: item?.user?.email,
            asset_id: item?.asset_id,
            provider_transaction_ref: item?.provider_transaction_ref,
            source: item?.source,
            transfer_type: item?.transfer_type,
            type: item?.type,
            fee: item?.fee,
            amount: item?.amount,
            currency: item?.asset?.code,
            status: item?.status,
            description: item?.description,
            canceled_reason: item?.canceled_reason ?? null,

            created_at: item?.created_at,
            updated_at: item?.updated_at,
          })),
        );
      },
      onError: (error: any) => {
        if (error.code === "ERR_NETWORK") {
          setAlertText(NetworkDisconnectMsg);
        } else {
          if (error.response) {
            setAlertText(getErrorMessage(error.response.data));
          } else {
            setAlertText(APISomethingWrongMsg);
          }
        }
        setIsAlertOpened(true);
      },
    },
  );

  const columns: ColumnsType<TransactionDataType> = [
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          ID
        </p>
      ),
      dataIndex: "id",
      className: "w-auto",
      key: "id",
      render: (value) => (
        <div className="flex flex-row gap-2 items-center">
          <p className="font-manrope text-secondary text-[13px] sm:text-[14px] font-medium">
            {value}
          </p>
        </div>
      ),
    },
    //provider_transaction_ref
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Provider transaction Id
        </p>
      ),
      dataIndex: "provider_transaction_ref",
      className: "w-auto",
      key: "provider_transaction_ref",
      render: (value) => (
        <div className="flex flex-row gap-2 items-center">
          <p className="font-manrope text-secondary text-[13px] sm:text-[14px] font-medium">
            {value}
          </p>
        </div>
      ),
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          User Id
        </p>
      ),
      dataIndex: "user_id",
      className: "w-auto",
      key: "user_id",
      render: (value) => (
        <div className="flex flex-row gap-2 items-center">
          <p className="font-manrope text-primary text-[13px] sm:text-[14px] font-medium">
            {value}
          </p>
        </div>
      ),
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          User Email
        </p>
      ),
      dataIndex: "email",
      className: "w-auto",
      key: "email",
      render: (value) => (
        <div className="flex flex-row gap-2 items-center">
          <p className="font-manrope text-primary text-[13px] sm:text-[14px] font-medium">
            {value ?? "__"}
          </p>
        </div>
      ),
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Provider
        </p>
      ),
      dataIndex: "provider_identifier",
      className: "w-auto",
      key: "provider_identifier",
      render: (value) => (
        <div className="flex flex-row gap-2 items-center">
          <p className="font-manrope text-primary text-[13px] sm:text-[14px] font-medium">
            {value ?? "__"}
          </p>
        </div>
      ),
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Source
        </p>
      ),
      dataIndex: "source",
      className: "w-auto",
      key: "source",
      render: (value) => (
        <div className="flex flex-row gap-2 items-center">
          <p className="font-manrope text-primary text-[13px] sm:text-[14px] font-medium">
            {value}
          </p>
        </div>
      ),
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Type
        </p>
      ),
      dataIndex: "type",
      className: "w-auto",
      key: "type",
      render: (value) => (
        <div className="flex flex-row gap-2 items-center">
          <p className="font-manrope text-primary text-[13px] sm:text-[14px] font-medium">
            {value ?? "__"}
          </p>
        </div>
      ),
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Transfer type
        </p>
      ),
      dataIndex: "transfer_type",
      className: "w-auto",
      key: "transfer_type",
      render: (value) => (
        <div className="flex flex-row gap-2 items-center">
          <p className="font-manrope text-primary text-[13px] sm:text-[14px] font-medium">
            {value ?? "__"}
          </p>
        </div>
      ),
    },

    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Amount
        </p>
      ),
      dataIndex: "amount",
      className: "w-auto",
      key: "amount",
      render: (value) => (
        <div className="flex flex-row gap-2 items-center">
          <p className="font-manrope text-primary text-[13px] sm:text-[14px] font-medium">
            {makeNumberWithFloatingDigits(value, 2)}
          </p>
        </div>
      ),
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Currency
        </p>
      ),
      dataIndex: "currency",
      className: "w-auto",
      key: "currency",
      render: (value) => (
        <div className="flex flex-row gap-2 items-center">
          <p className="font-manrope text-primary text-[13px] sm:text-[14px] font-medium">
            {value.toUpperCase() ?? "__"}
          </p>
        </div>
      ),
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Date
        </p>
      ),
      dataIndex: "created_at",
      className: "w-auto",
      key: "created_at",
      render: (value) => (
        <div className="flex flex-row gap-2 items-center">
          <p className="font-manrope text-secondary text-[13px] sm:text-[14px] font-medium">
            {/* {moment.unix(value).format("YYYY-MM-DD HH:mm:ss")} */}
            {moment.utc(value).local().format("YYYY-MM-DD HH:mm:ss")}{" "}
          </p>
        </div>
      ),
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          {/* {L.translate("TransactionHistory.status")} */}
          Status
        </p>
      ),
      dataIndex: "status",
      className: "w-auto",
      key: "status",
      render: (value) => (
        <div className="flex flex-row gap-2 items-center">
          <p className="font-manrope text-secondary text-[13px] sm:text-[14px] font-medium">
            {/* {getPaymentStatusName(fetchStatus(value).toLowerCase())} */}
            {/* {value?.toLowerCase() === "ok" ? "Success" : "Failed"} */}
            {value}
          </p>
        </div>
      ),
    },
    {
      title: () => (
        <p className="font-manrope text-secondary text-[14px] font-medium">
          Cancel Reason
        </p>
      ),
      dataIndex: "canceled_reason",
      className: "w-auto",
      key: "canceled_reason",
      render: (_, record) => (
        <div className="flex flex-row gap-2 items-center">
          <p className="font-manrope text-primary text-[13px] sm:text-[14px] font-medium">
            {record.status === "failed" || record.status === "rejected"
              ? _ || "—"
              : "—"}{" "}
          </p>
        </div>
      ),
    },
    // {
    //   title: () => (
    //     <p className="font-manrope text-secondary text-[14px] font-medium">
    //       Receipt
    //     </p>
    //   ),
    //   dataIndex: "receipt",
    //   className: "w-auto",
    //   key: "receipt",
    //   render: (_, record) =>
    //     record.status?.toLowerCase() !== "completed" ? (
    //       <p className="text-gray-400 text-[12px] font-manrope italic">---</p>
    //     ) : downloadingId === Number(record.id) ? (
    //       <p className="text-gray-400 text-[12px] font-manrope italic">
    //         Downloading...
    //       </p>
    //     ) : (
    //       <a
    //         onClick={() => receiptBtn(record)}
    //         className="text-secondary underline hover:text-primary text-[12px] font-manrope font-medium cursor-pointer"
    //       >
    //         Receipt
    //       </a>
    //     ),
    // },
  ];

  return (
    <div className="w-full">
      <div className="custom-scroll flex flex-col w-full bg-white rounded-[12px] p-4 mt-2 sm:mt-4">
        <Table
          loading={isLoading}
          showSorterTooltip={false}
          columns={columns}
          dataSource={dataSource}
          pagination={{
            total,
            current: searchParams.page,
            onChange: onPageChange,
            position: ["bottomCenter"],
            defaultPageSize: LIST_PAGE_SIZE,
            showSizeChanger: false,
          }}
        />
      </div>
    </div>
  );
};

export default TransactionsCryptoWalletPage;
